/* TODO: t() */
import React from 'react';
import { StyleProp, StyleSheet } from 'react-native';

import {
  HStack,
  Modal as ChakraModal,
  ModalOverlay,
  ModalOverlayProps,
  ModalContent,
  ModalHeader,
  ModalHeaderProps,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
} from '@chakra-ui/react';

type PropStyles = {
  closeButtonStyle?: React.CSSProperties;
  overlayStyle?: ModalOverlayProps['style'];
  titleRow?: boolean;
  titleStyle?: ModalHeaderProps['style'];
};

type Props = PropStyles & {
  title?: string;
  isVisible: boolean;
  children?: React.ReactNode;
  onClose?: () => void;
  style?: StyleProp<React.CSSProperties>;
  canEscapeKeyClose?: boolean;
  isCloseButtonShown?: boolean;
  canOutsideClickClose?: boolean;
  scrollBehavior?: 'inside' | 'outside';
};

function Modal(props: Props) {
  let {
    title,
    isVisible,
    children,
    onClose,
    style,
    canEscapeKeyClose = true,
    isCloseButtonShown = true,
    canOutsideClickClose = false,
    scrollBehavior = 'inside',
    closeButtonStyle,
    overlayStyle,
    titleRow,
    titleStyle,
  } = props;

  return (
    <ChakraModal
      closeOnOverlayClick={canOutsideClickClose}
      autoFocus={true}
      isCentered={true}
      scrollBehavior={scrollBehavior}
      onEsc={() => {
        canEscapeKeyClose && onClose?.();
      }}
      isOpen={isVisible}
      onClose={() => onClose?.()}
    >
      <ModalOverlay style={overlayStyle} />
      <ModalContent
        // Technically the size should be managed by the `size` prop on the
        // ChakraModal above. See https://chakra-ui.com/docs/overlay/modal#modal-sizes
        // But I'm leaving it here for now so that style prop works correctly.
        w={500}
        maxW="100vw"
        style={StyleSheet.flatten(style)}
      >
        {title ? (
          titleRow ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                flexDirection: 'row',
              }}
            >
              <ModalHeader style={titleStyle}>{title}</ModalHeader>
              {isCloseButtonShown && (
                <ModalCloseButton style={closeButtonStyle} />
              )}
            </div>
          ) : (
            <>
              <ModalHeader style={titleStyle}>{title}</ModalHeader>
              {isCloseButtonShown && (
                <ModalCloseButton style={closeButtonStyle} />
              )}
            </>
          )
        ) : null}
        {children}
      </ModalContent>
    </ChakraModal>
  );
}

function Header(props: Pick<Props, 'title' | 'isCloseButtonShown'>) {
  let { title, isCloseButtonShown = true } = props;
  return (
    <>
      <ModalHeader>{title}</ModalHeader>
      {isCloseButtonShown && <ModalCloseButton />}
    </>
  );
}

function Body(props: Pick<Props, 'children' | 'style'>) {
  let { children, style } = props;
  return <ModalBody style={StyleSheet.flatten(style)}>{children}</ModalBody>;
}

function Footer(props: Pick<Props, 'children' | 'style'>) {
  let { children, style } = props;
  return (
    <ModalFooter>
      <HStack style={StyleSheet.flatten(style)}>{children}</HStack>
    </ModalFooter>
  );
}

Modal.Header = Header;
Modal.Body = Body;
Modal.Footer = Footer;

export default Object.assign(Modal, {
  Header,
  Body,
  Footer,
}) as typeof Modal;

export { Props as ModalProps };
